import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, Loader2, Megaphone, Clock, Tag, ChevronDown, Paperclip, FileText, Image as ImageIcon, Trash2, Download } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Attachment {
  name: string;
  url: string;
  type: string;
  size: number;
}

interface PortalUpdate {
  id: string;
  category: string;
  title: string;
  content: string | null;
  attachments: Attachment[];
  created_at: string;
  created_by: string | null;
}

interface PortalPanelProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
  isSuperAdmin?: boolean;
  onRead?: () => void;
}

const CATEGORIES = [
  { value: 'seo', label: 'SEO', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'ads', label: 'Ad Management', color: 'bg-blue-100 text-blue-700' },
  { value: 'web', label: 'Web Development', color: 'bg-purple-100 text-purple-700' },
  { value: 'social', label: 'Social Media', color: 'bg-pink-100 text-pink-700' },
  { value: 'branding', label: 'Branding', color: 'bg-amber-100 text-amber-700' },
  { value: 'general', label: 'General', color: 'bg-gray-100 text-gray-700' },
];

function getCategoryStyle(value: string) {
  return CATEGORIES.find(c => c.value === value) || CATEGORIES[CATEGORIES.length - 1];
}

function isImageType(type: string) {
  return type.startsWith('image/');
}

function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function linkifyContent(text: string) {
  const urlRegex = /(https?:\/\/[^\s<]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) =>
    urlRegex.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline underline-offset-2 break-all">
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

function formatRelativeDate(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

export default function PortalPanel({ isOpen, onClose, businessId, businessName, isSuperAdmin = false, onRead }: PortalPanelProps) {
  const [updates, setUpdates] = useState<PortalUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('general');
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const categoryRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && businessId) {
      loadUpdates();
    }
  }, [isOpen, businessId]);

  useEffect(() => {
    if (!showCategoryPicker) return;
    const handleClick = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setShowCategoryPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCategoryPicker]);

  const loadUpdates = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('portal_updates')
      .select('*')
      .eq('business_id', businessId)
      .order('created_at', { ascending: false });

    if (!error) setUpdates(data || []);
    setLoading(false);

    // Mark as read (only for business users, not super admin)
    if (!isSuperAdmin) {
      await supabase
        .from('portal_reads')
        .upsert({ business_id: businessId, last_read_at: new Date().toISOString() }, { onConflict: 'business_id' });
      onRead?.();
    }
  };

  const uploadFiles = async (files: File[]): Promise<Attachment[]> => {
    const uploaded: Attachment[] = [];
    for (const file of files) {
      const fileExt = file.name.split('.').pop() || 'bin';
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `portal/${businessId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('user-uploads')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (uploadError) {
        console.error('Upload failed:', uploadError.message);
        continue;
      }

      const { data: urlData } = supabase.storage
        .from('user-uploads')
        .getPublicUrl(filePath);

      uploaded.push({
        name: file.name,
        url: urlData.publicUrl,
        type: file.type,
        size: file.size,
      });
    }
    return uploaded;
  };

  const handlePost = async () => {
    if (!title.trim() || posting) return;
    setPosting(true);

    let attachments: Attachment[] = [];
    if (pendingFiles.length > 0) {
      attachments = await uploadFiles(pendingFiles);
    }

    const { error } = await supabase
      .from('portal_updates')
      .insert({
        business_id: businessId,
        category,
        title: title.trim(),
        content: content.trim() || null,
        attachments,
      });

    if (error) {
      console.error('Portal update insert failed:', error);
      alert(`Failed to post update: ${error.message}`);
    } else {
      setTitle('');
      setContent('');
      setCategory('general');
      setPendingFiles([]);
      await loadUpdates();
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }

    setPosting(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setPendingFiles(prev => [...prev, ...files]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from('portal_updates').delete().eq('id', id);
    if (!error) setUpdates(prev => prev.filter(u => u.id !== id));
  };

  if (!isOpen) return null;

  const selectedCat = getCategoryStyle(category);

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100050] transition-opacity duration-200"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="fixed inset-y-0 right-0 w-full md:w-[600px] bg-white shadow-2xl z-[100051] flex flex-col transform transition-transform duration-200 translate-x-0"
        style={{
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
              <Megaphone className="h-[18px] w-[18px] text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 truncate">Portal</h2>
              <p className="text-xs text-gray-500 truncate">{businessName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Updates Feed */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto bg-gray-50">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
            </div>
          ) : updates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
              <div className="w-14 h-14 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <Megaphone className="h-7 w-7 text-gray-300" />
              </div>
              <p className="text-sm font-medium text-gray-500">No updates yet</p>
              <p className="text-xs text-gray-400 mt-1">
                {isSuperAdmin ? 'Post an update for this business below.' : 'Updates from your service provider will appear here.'}
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {updates.map((update) => {
                const cat = getCategoryStyle(update.category);
                return (
                  <div key={update.id} className="bg-white rounded-xl border border-gray-200/80 shadow-sm p-4 group">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${cat.color}`}>
                          <Tag className="h-3 w-3 mr-1" />
                          {cat.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-[11px] text-gray-400 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatRelativeDate(update.created_at)}
                        </span>
                        {isSuperAdmin && (
                          <button
                            onClick={() => handleDelete(update.id)}
                            className="p-1 text-gray-300 hover:text-red-500 rounded transition-colors opacity-0 group-hover:opacity-100"
                            title="Delete update"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <h4 className="text-sm font-semibold text-gray-900">{update.title}</h4>
                    {update.content && (
                      <p className="text-sm text-gray-600 mt-1 leading-relaxed whitespace-pre-wrap">{linkifyContent(update.content)}</p>
                    )}
                    {update.attachments && update.attachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {/* Image attachments as thumbnails */}
                        {update.attachments.filter(a => isImageType(a.type)).length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {update.attachments.filter(a => isImageType(a.type)).map((att, i) => (
                              <a
                                key={i}
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block w-24 h-24 rounded-lg overflow-hidden border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all"
                              >
                                <img src={att.url} alt={att.name} className="w-full h-full object-cover" />
                              </a>
                            ))}
                          </div>
                        )}
                        {/* Document attachments as links */}
                        {update.attachments.filter(a => !isImageType(a.type)).map((att, i) => (
                          <a
                            key={i}
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-colors"
                          >
                            <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <span className="text-xs text-gray-700 font-medium truncate flex-1">{att.name}</span>
                            <span className="text-[10px] text-gray-400 flex-shrink-0">{formatFileSize(att.size)}</span>
                            <Download className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Compose Area (super admin only) */}
        {isSuperAdmin && (
          <div className="border-t border-gray-200 bg-white p-4 flex-shrink-0">
            {/* Category picker */}
            <div className="relative mb-3" ref={categoryRef}>
              <button
                onClick={() => setShowCategoryPicker(!showCategoryPicker)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${selectedCat.color} border-transparent hover:opacity-80`}
              >
                <Tag className="h-3 w-3" />
                {selectedCat.label}
                <ChevronDown className="h-3 w-3" />
              </button>
              {showCategoryPicker && (
                <div className="absolute bottom-full left-0 mb-1 bg-white border border-gray-200 rounded-xl shadow-lg py-1 min-w-[160px] z-10">
                  {CATEGORIES.map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => { setCategory(cat.value); setShowCategoryPicker(false); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors flex items-center gap-2 ${
                        category === cat.value ? 'font-medium text-gray-900' : 'text-gray-600'
                      }`}
                    >
                      <span className={`inline-block w-2 h-2 rounded-full ${cat.color.split(' ')[0]}`} />
                      {cat.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Update title..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none mb-2"
            />
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Details (optional)..."
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none resize-none mb-2"
            />

            {/* Pending files preview */}
            {pendingFiles.length > 0 && (
              <div className="mb-2 space-y-1.5">
                {pendingFiles.map((file, i) => (
                  <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
                    {isImageType(file.type) ? (
                      <ImageIcon className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
                    ) : (
                      <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                    )}
                    <span className="text-xs text-gray-700 truncate flex-1">{file.name}</span>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">{formatFileSize(file.size)}</span>
                    <button
                      onClick={() => removePendingFile(i)}
                      className="p-0.5 text-gray-400 hover:text-red-500 rounded transition-colors flex-shrink-0"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Actions row */}
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                title="Attach files"
              >
                <Paperclip className="h-3.5 w-3.5" />
                Attach
              </button>
              <button
                onClick={handlePost}
                disabled={!title.trim() || posting}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {posting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Posting...</>
                ) : (
                  <><Send className="h-4 w-4" /> Post Update</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </>,
    document.body
  );
}
